import { ApplicationInsights } from "@microsoft/applicationinsights-web";

const connectionString = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING;
const cloudRole = process.env.REACT_APP_APPINSIGHTS_CLOUD_ROLE || "nimblelab-ui";
const appVersion = process.env.REACT_APP_APP_VERSION || "unknown";
const samplingPercentage = Number(process.env.REACT_APP_APPINSIGHTS_SAMPLING_PERCENTAGE || 100);
const runtimeEnvironment = process.env.NODE_ENV || "unknown";
const sessionStorageKey = "nimblelab-telemetry-session-id";

let appInsightsClient;

const sanitizeValue = (value) => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (["string", "number", "boolean"].includes(typeof value)) {
    return typeof value === "string" ? value.slice(0, 300) : value;
  }

  return JSON.stringify(value).slice(0, 300);
};

const getSessionId = () => {
  try {
    const existingSessionId = sessionStorage.getItem(sessionStorageKey);

    if (existingSessionId) {
      return existingSessionId;
    }

    const nextSessionId = window?.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    sessionStorage.setItem(sessionStorageKey, nextSessionId);

    return nextSessionId;
  } catch {
    return "unavailable";
  }
};

const getGlobalProperties = () => ({
  appVersion,
  cloudRole,
  environment: runtimeEnvironment,
  sessionId: getSessionId(),
});

const normalizeProperties = (properties = {}) => {
  const normalized = {
    ...getGlobalProperties(),
  };

  Object.keys(properties).forEach((key) => {
    const sanitized = sanitizeValue(properties[key]);

    if (sanitized !== undefined) {
      normalized[key] = sanitized;
    }
  });

  return normalized;
};

const normalizeMeasurements = (measurements = {}) => {
  const normalized = {};

  Object.keys(measurements).forEach((key) => {
    const value = Number(measurements[key]);

    if (Number.isFinite(value)) {
      normalized[key] = value;
    }
  });

  return normalized;
};

const hashIdentifier = (value = "") => {
  let hash = 0;

  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }

  return `user_${Math.abs(hash)}`;
};

const toTimeSpan = (durationMs = 0) => {
  const safeDuration = Number.isFinite(durationMs) ? Math.max(0, durationMs) : 0;
  const hours = Math.floor(safeDuration / 3600000);
  const minutes = Math.floor((safeDuration % 3600000) / 60000);
  const seconds = Math.floor((safeDuration % 60000) / 1000);
  const milliseconds = Math.floor(safeDuration % 1000);

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(milliseconds).padStart(3, "0")}`;
};

const getTargetFromUrl = (url = "") => {
  try {
    const resolved = new URL(url, window.location.origin);

    return resolved.host;
  } catch {
    return window.location.host;
  }
};

const getPathFromUrl = (url = "") => {
  try {
    const resolved = new URL(url, window.location.origin);

    return resolved.pathname;
  } catch {
    return url;
  }
};

const createCorrelationId = () => {
  if (window?.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const initializeAppInsights = () => {
  if (appInsightsClient) {
    return appInsightsClient;
  }

  if (!connectionString) {
    return null;
  }

  appInsightsClient = new ApplicationInsights({
    config: {
      connectionString,
      disableAjaxTracking: true,
      enableAutoRouteTracking: false,
      enableCorsCorrelation: true,
      autoTrackPageVisitTime: true,
      samplingPercentage,
    },
  });

  appInsightsClient.loadAppInsights();

  appInsightsClient.addTelemetryInitializer((envelope) => {
    envelope.tags = envelope.tags || {};
    envelope.tags["ai.cloud.role"] = cloudRole;
    envelope.tags["ai.application.ver"] = appVersion;

    envelope.data = envelope.data || {};
    envelope.data.environment = runtimeEnvironment;
  });

  return appInsightsClient;
};

const getClient = () => initializeAppInsights();

export const getAppInsightsClient = () => getClient();

export const getNewCorrelationId = () => createCorrelationId();

export const trackPageView = (path, properties = {}) => {
  const client = getClient();

  if (!client) {
    return;
  }

  const resolvedPath = path || window.location.pathname;

  client.trackPageView(
    {
      name: document.title,
      uri: `${window.location.origin}${resolvedPath}`,
    },
    normalizeProperties({
      path: resolvedPath,
      ...properties,
    })
  );
};

export const trackEvent = (name, properties = {}, measurements = {}) => {
  const client = getClient();

  if (!client) {
    return;
  }

  client.trackEvent(
    { name },
    normalizeProperties(properties),
    normalizeMeasurements(measurements)
  );
};

export const trackException = (error, properties = {}) => {
  const client = getClient();

  if (!client || !error) {
    return;
  }

  client.trackException({ exception: error }, normalizeProperties(properties));
};

export const trackWebVital = (metric) => {
  const client = getClient();

  if (!client || !metric) {
    return;
  }

  client.trackMetric(
    {
      name: `web_vital_${metric.name}`,
      average: metric.value,
      sampleCount: 1,
    },
    normalizeProperties({
      id: metric.id,
      rating: metric.rating,
      navigationType: metric.navigationType,
    })
  );
};

export const trackApiDependency = ({
  method = "GET",
  url = "",
  status = 0,
  duration = 0,
  success = false,
  correlationId,
  properties = {},
}) => {
  const client = getClient();

  if (!client) {
    return;
  }

  const requestPath = getPathFromUrl(url);
  const requestMethod = method.toUpperCase();
  const dependencyId = correlationId || createCorrelationId();

  client.trackDependencyData(
    {
      id: dependencyId,
      target: getTargetFromUrl(url),
      name: `${requestMethod} ${requestPath}`,
      duration: toTimeSpan(duration),
      success,
      responseCode: String(status || 0),
      dependencyTypeName: "HTTP",
      data: url,
      startTime: new Date(Date.now() - Math.max(0, duration)),
    },
    normalizeProperties({
      correlationId: dependencyId,
      dependencyKind: "frontend_http",
      method: requestMethod,
      path: requestPath,
      ...properties,
    })
  );
};

export const setTelemetryUserContext = (userInfo = {}) => {
  const client = getClient();

  if (!client) {
    return;
  }

  const identifier = userInfo?.sAMAccountName || userInfo?.email || userInfo?.preferred_username;

  if (!identifier) {
    client.clearAuthenticatedUserContext();
    return;
  }

  client.setAuthenticatedUserContext(hashIdentifier(identifier), undefined, true);
};

export const clearTelemetryUserContext = () => {
  const client = getClient();

  if (!client) {
    return;
  }

  client.clearAuthenticatedUserContext();
};
